import { Component, EventEmitter, Input, Output } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ProjectService } from "app/services/project/project.service";
import mime from "mime";
import { map } from "rxjs/operators";
/** Document attached to a communication (canal_denuncias_comunicaciones_documents) */
export interface ComunicacionDocumentoData {
    hash: string;
    idComunicacion: number;
    contentType: string | null;
    fileName: string | null;
    size: number;
    icon_class: string;
    nombreDocumento: string | null;
    tipo: number;
}

/** Message/communication item from canal_denuncias_comunicaciones */
export interface ComunicacionMensajeData {
    id: number;
    hashDenuncia: string;
    creationDate: string;
    actor: number;
    mensaje: string | null;
    leidoDenunciante: number;
    leidoRRHH: number;
    idRespRRHH: number | null;
    visible: number;
    documents?: ComunicacionDocumentoData[];
}

@Component({
    selector: "app-comunicacion-mensaje",
    templateUrl: "./comunicacion-mensaje.component.html",
    styleUrls: ["./comunicacion-mensaje.component.scss"],
    standalone: false
})
export class ComunicacionMensajeComponent {
    /** Full message/communication object from the API */
    @Input() mensaje: ComunicacionMensajeData | null = null;
    @Input() side: string;
    @Input() permisos: any;
    @Input() isSugerencia: any;

    /** Emitted when a message is successfully edited or deleted so the parent can refresh the list */
    @Output() mensajeActualizado = new EventEmitter<void>();

    constructor(
        private projectSvc: ProjectService,
        private snackBar: MatSnackBar
    ) {}

    /** Collapsed by default */
    documentosExpanded = false;

    /** Edit mode: show textarea + buttons */
    editingMessage = false;
    editedMensajeText = "";

    /** Min/max length for message (same as new message) */
    readonly MIN_MENSAJE = 20;
    readonly MAX_MENSAJE = 1000;

    /** Delete: show confirmation before proceeding */
    showDeleteConfirm = false;

    /** True if edited message is valid (20–1000 chars trimmed) and can submit */
    canSubmitEdit(): boolean {
        const len = (this.editedMensajeText ?? "").trim().length;
        return len >= this.MIN_MENSAJE && len <= this.MAX_MENSAJE;
    }

    /** True when edit/delete icons should be shown: RRHH and leidoDenunciante===0, or DENUNCIANTE and leidoRRHH===0 */
    canEditOrDelete(): boolean {
        if (!this.mensaje) return false;
        if (this.mensaje.actor === 2) return false; // system message
        if (this.side === "RRHH" && this.mensaje.leidoDenunciante === 0 && this.permisos?.w == 1) return true;
        if (this.side === "DENUNCIANTE" && this.mensaje.leidoRRHH === 0) return true;
        return false;
    }

    startEditing(): void {
        this.editingMessage = true;
        this.editedMensajeText = this.mensaje?.mensaje ?? "";
    }

    cancelEditing(): void {
        this.editingMessage = false;
        this.editedMensajeText = "";
    }

    submitModificacion(): void {
        if (!this.canSubmitEdit()) return;
        if (this.side === "RRHH") {
            this.projectSvc
                .editarMensajeDenunciaPrivate(
                    this.mensaje?.id ?? 0,
                    this.mensaje?.hashDenuncia ?? "",
                    this.editedMensajeText.trim()
                )
                .subscribe({
                    next: (response: any) => {
                        if (response?.success) {
                            this.snackBar.open(response.message ?? "Mensaje actualizado", "Cerrar", {
                                duration: 3000
                            });
                            this.editingMessage = false;
                            this.editedMensajeText = "";
                            this.mensajeActualizado.emit();
                        } else {
                            this.snackBar.open(response?.message ?? "Error al actualizar", "Cerrar", {
                                duration: 3000
                            });
                        }
                    },
                    error: (err) => {
                        this.snackBar.open(err?.error?.message ?? "Error al actualizar el mensaje", "Cerrar", {
                            duration: 3000
                        });
                    }
                });
        } else {
            this.projectSvc
                .editarMensajeDenunciaPublic(
                    this.mensaje?.id ?? 0,
                    this.mensaje?.hashDenuncia ?? "",
                    this.editedMensajeText.trim()
                )
                .subscribe({
                    next: (response: any) => {
                        if (response?.success) {
                            this.snackBar.open(response.message ?? "Mensaje actualizado", "Cerrar", {
                                duration: 3000
                            });
                            this.editingMessage = false;
                            this.editedMensajeText = "";
                            this.mensajeActualizado.emit();
                        } else {
                            this.snackBar.open(response?.message ?? "Error al actualizar", "Cerrar", {
                                duration: 3000
                            });
                        }
                    },
                    error: (err) => {
                        this.snackBar.open(err?.error?.message ?? "Error al actualizar el mensaje", "Cerrar", {
                            duration: 3000
                        });
                    }
                });
        }
        this.editingMessage = false;
        this.editedMensajeText = "";
    }

    onEliminarClick(): void {
        this.showDeleteConfirm = true;
    }

    cancelDeleteConfirm(): void {
        this.showDeleteConfirm = false;
    }

    confirmarEliminacion(): void {
        this.showDeleteConfirm = false;
        if (this.side === "RRHH") {
            this.projectSvc
                .eliminarMensajeDenunciaPrivate(this.mensaje?.id ?? 0, this.mensaje?.hashDenuncia ?? "")
                .subscribe({
                    next: (response: any) => {
                        if (response?.success) {
                            this.snackBar.open(response.message ?? "Mensaje eliminado", "Cerrar", {
                                duration: 3000
                            });
                            this.mensajeActualizado.emit();
                        } else {
                            this.snackBar.open(response?.message ?? "Error al eliminar", "Cerrar", {
                                duration: 3000
                            });
                        }
                    },
                    error: (err) => {
                        this.snackBar.open(err?.error?.message ?? "Error al eliminar el mensaje", "Cerrar", {
                            duration: 3000
                        });
                    }
                });
        } else {
            this.projectSvc
                .eliminarMensajeDenunciaPublic(this.mensaje?.id ?? 0, this.mensaje?.hashDenuncia ?? "")
                .subscribe({
                    next: (response: any) => {
                        if (response?.success) {
                            this.snackBar.open(response.message ?? "Mensaje eliminado", "Cerrar", {
                                duration: 3000
                            });
                            this.mensajeActualizado.emit();
                        } else {
                            this.snackBar.open(response?.message ?? "Error al eliminar", "Cerrar", {
                                duration: 3000
                            });
                        }
                    },
                    error: (err) => {
                        this.snackBar.open(err?.error?.message ?? "Error al eliminar el mensaje", "Cerrar", {
                            duration: 3000
                        });
                    }
                });
        }
    }

    readonly tiposDocumento: Array<{ id: number; nombre: string }> = [
        { id: 0, nombre: "Documento contractual o laboral" },
        { id: 1, nombre: "Comunicación" },
        { id: 2, nombre: "Informe o declaración" },
        { id: 3, nombre: "Evidencia técnica" },
        { id: 4, nombre: "Documento económico o financiero" },
        { id: 5, nombre: "Documento médico o psicológico" },
        { id: 6, nombre: "Resolución o actuación interna" },
        { id: 9, nombre: "Otros" }
    ];

    /** Date format DD/MM/AAAA HH:mm:ss */
    formatDate(date: string | null | undefined): string {
        if (!date) return "";
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = String(d.getSeconds()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    /** Header label by actor: 0 = denunciante, 1 = gestor, 2 = sistema, 3 = denunciante modificado */
    getHeaderLabel(actor: number): string {
        if (this.isSugerencia) {
            if (actor === 0) return "Mensaje del solicitante";
            if (actor === 1) return "Mensaje del Gestor";
            if (actor === 2) return "Mensaje del sistema";
            if (actor === 3) return "Mensaje modificado por el solicitante.";
            return "Mensaje";
        } else {
            if (actor === 0) return "Mensaje del denunciante";
            if (actor === 1) return "Mensaje del Gestor";
            if (actor === 2) return "Mensaje del sistema";
            if (actor === 3) return "Mensaje modificado por el denunciante.";
            return "Mensaje";
        }
    }

    toggleDocumentos(): void {
        this.documentosExpanded = !this.documentosExpanded;
    }

    getDocumentosCount(): number {
        return this.mensaje?.documents?.length ?? 0;
    }

    getTipoDocumentoName(tipo: number | undefined | null): string {
        if (tipo == null || typeof tipo !== "number") return "-";
        const t = this.tiposDocumento.find((x) => x.id === tipo);
        return t ? t.nombre : "-";
    }

    formatFileSize(bytes: number): string {
        if (!bytes || bytes === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    }

    onVerDocumento(doc: ComunicacionDocumentoData): void {
        const mimeType = mime.getType(doc.fileName) || "application/octet-stream";
        this.projectSvc
            .getOneDocumentFromDenunciaComunicacionExternal(doc.hash)
            .pipe(map((response: any) => new Blob([response], { type: mimeType })))
            .subscribe((blob: Blob) => {
                const fileURL = URL.createObjectURL(blob);
                window.open(fileURL, "_blank");
            });
    }
}
